import { Box, Switch, Tooltip } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useId } from "react";

/**
 * One preference switch, with its explanation attached where a screen reader will
 * actually read it.
 *
 * Every switch on this page needs an accessible name of its own: the page is a
 * grid of identical controls whose meaning comes entirely from the row and column
 * they sit in, and a screen-reader user hearing "switch, switch, switch" has no
 * way to tell which alert they are muting.
 *
 * The harder problem is the explanation. The most important thing this page says
 * is "this switch is on and nothing is being delivered" — the state that exists
 * because a master silences without clearing. A tooltip alone says that to a mouse
 * and to nobody else, so the same words are also rendered as a description of the
 * `<input>`.
 *
 * Two details are load-bearing and both are easy to get wrong:
 *
 * - `role: "switch"` is repeated in `slotProps.input` rather than inherited. MUI's
 *   `Switch` sets it through the same channel, so passing our own label *replaces*
 *   it, silently downgrading the control to a plain checkbox.
 * - The description hangs off the `<input>`, never off the element MUI's `Tooltip`
 *   would decorate. `Tooltip` applies a string `title` as an `aria-label` on its
 *   child, and its child here is the `MuiSwitch-switchBase` span — a `generic`
 *   element, where an accessible name is *prohibited* and therefore discarded.
 *   Passing the title as a node instead, with `describeChild`, makes `Tooltip`
 *   contribute only a transient `aria-describedby` and no name at all, leaving the
 *   input's own name and description as the single source of what is announced.
 */

export type PreferenceSwitchProps = {
  /** The switch's accessible name: which alert, and which channel. */
  label: string;
  /**
   * Where this position came from, or why a switch that is on is delivering
   * nothing. Shown on hover and announced as the control's description.
   */
  explanation: string;
  /**
   * The stored position, not the delivered value. Under a mute the two differ, and
   * rendering the delivered value would both lie about what is saved and offer to
   * save that lie back.
   */
  checked: boolean;
  /**
   * Set while a write is in flight. Never used for a switch that is merely muted
   * from above: a muted switch is still the reader's to change, and disabling it
   * would take away the choices the mute exists to preserve.
   */
  disabled?: boolean;
  /** MUI switch size; section toggles are small so they sit inline with a heading. */
  size?: "small" | "medium";
  onChange: (enabled: boolean) => void;
};

export function PreferenceSwitch({
  label,
  explanation,
  checked,
  disabled,
  size,
  onChange,
}: PreferenceSwitchProps) {
  const descriptionId = useId();

  return (
    <>
      <Tooltip describeChild title={<span>{explanation}</span>}>
        <Switch
          checked={checked}
          disabled={disabled}
          size={size}
          onChange={(event) => onChange(event.target.checked)}
          slotProps={{
            input: { role: "switch", "aria-label": label, "aria-describedby": descriptionId },
          }}
        />
      </Tooltip>
      {/*
        Rendered text rather than an `aria-label`, and out of flow rather than
        hidden: `aria-hidden` or `display: none` content cannot be referenced by
        `aria-describedby`, and this is the only route the explanation has to a
        reader who is not holding a mouse.
      */}
      <Box component="span" id={descriptionId} sx={visuallyHidden}>
        {explanation}
      </Box>
    </>
  );
}
